// App — login gate + three-pane workbench + multi-project + i18n/theme.
// Boot: GET /api/me → 200 goes to the workbench / 401 goes to the login page. Any non-login /api 401 → back to login (callback in api.ts).

import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import type Konva from 'konva';
import Artboard from './components/Artboard';
import ChatPanel from './components/ChatPanel';
import SidePanel from './components/SidePanel';
import PropsFloat from './components/PropsFloat';
import LoginPage, { MoonIcon, SunIcon } from './components/LoginPage';
import AdminPanel from './components/AdminPanel';
import ProjectSwitcher from './components/ProjectSwitcher';
import HomeGallery from './components/HomeGallery';
import PageBar from './components/PageBar';
import { PlusIcon, ChatIcon, FrameIcon, LayersIcon, PlayIcon, StopIcon, AlertIcon, XIcon, UndoIcon, RedoIcon, ChevronLeftIcon, ChevronRightIcon, MenuIcon } from './components/icons';
import { currentPage, currentProject, initialState, isDefaultProjectName, makeProject, parsePersistedDoc, persist, reducer, restore, applyOps } from './store';
import { cancelTurn, fetchActiveTurns, fetchDoc, fetchMe, isAbortError, onUnauthorized, pollJob, pollTurn, postCritique, postLogout, postRegionEdit, postReplicateDiff, pullDocWithRetry, putDoc, retryJob, submitChat, uploadImage, type CritiqueIssue, type CritiqueResult, type ReplicateDiffResult, type SubmitChatExtra } from './api';
import { docFingerprint, planPush, putDocWithRetry, readLastSyncedFingerprint, runBootReconcile, writeLastSyncedFingerprint, type SyncStatus } from './lib/cloudSync';
import { compositeRegion, cropRegion, naturalSize, type RegionRect } from './lib/imgtools';
import { relayCompress } from './lib/imgRelay';
import { extractPaletteFromUrl } from './lib/palette';
import { isNearEmptyCanvas, isNoRefReplicaTurn, isReplicateIntent, snapArtboardPreset } from './lib/replicate';
import {
  JOBS_SETTLE_POLL_MS, JOBS_SETTLE_TIMEOUT_MS, NO_CHANGE_SUMMARY, REFINE_BUDGET_VISUAL_DEFAULT,
  SCREENSHOT_TTL_DAYS, SELECTION_SETTLE_MS, SETTLE_RENDER_DELAY_MS,
  arbitrateCritique, buildRefineInstruction, buildResidual, detectRegression, isBigChange, isVerdictFrozen, mergeReauditReport, refineBudget, sameHardFingerprints, shouldEnterLoop, sleep,
  // SPEC-461 M3.2（RC3 根治）: 异步落地补审 — 触发判定纯函数 + 末次审查档案类型
  shouldFinalReaudit, type LastReviewRecord,
  // SPEC-462 M4（收敛再诊断）: 跨轮 finding 账本 + stuck 判定 + 再归因推迟熔断判定 + 已试 ops 摘要
  reattributionDefersFreeze, stuckFindings, summarizeTriedOps, updateFindingRounds, type FindingRounds, type ReattributionInfo,
} from './lib/agentLoop';
import { buildDoneText } from './lib/doneText';
import { requireReviewPhase, type ReviewPhase } from './lib/reviewPhase';
import RegionComposer from './components/RegionComposer';
import { translate, getLang, toggleLang, useT } from './i18n';
import { toggleTheme, useTheme } from './theme';
import type { AgentResult, CanvasObject, CanvasOp, CanvasState, ChatMsg, ChatProcessStep, TurnProgress, TurnRecord } from './types';

type AuthState = 'unknown' | 'anon' | 'authed';

/** ≤768px (same cutoff as the mobile breakpoint in styles.css). Narrow screens force panels expanded (a collapsed rail is pointless in the single-column tab layout). */
// Region-edit input constraints (SPEC-379): the edit model rejects inputs below 240x240; crops are upscaled
// to meet it, up to this cap — beyond that the region is genuinely too small and gets a friendly error.
const MIN_EDIT_PX = 240;
const MAX_CROP_UPSCALE = 6;

// Gallery thumbnail (SPEC-404): downsampled from the full-res clean screenshot — long edge ≤420px,
// JPEG quality 0.7, dropped entirely above ~150KB (card falls back to a placeholder).
const THUMB_MAX_EDGE = 420;
const THUMB_MAX_BYTES = 150 * 1024;

/** Downscale a full-res screenshot into a small JPEG data URL; undefined = drop (oversized / failed). */
function downscaleThumb(pngDataUrl: string): Promise<string | undefined> {
  return new Promise(resolve => {
    const img = new Image();
    img.onload = () => {
      try {
        const ratio = Math.min(1, THUMB_MAX_EDGE / Math.max(img.width, img.height));
        const w = Math.max(1, Math.round(img.width * ratio));
        const h = Math.max(1, Math.round(img.height * ratio));
        const c = document.createElement('canvas');
        c.width = w; c.height = h;
        const ctx = c.getContext('2d');
        if (!ctx) return resolve(undefined);
        ctx.drawImage(img, 0, 0, w, h);
        const url = c.toDataURL('image/jpeg', 0.7);
        // base64 length → approx bytes; oversized thumbs are dropped rather than bloating the doc
        resolve(url.length * 0.75 <= THUMB_MAX_BYTES ? url : undefined);
      } catch { resolve(undefined); }
    };
    img.onerror = () => resolve(undefined);
    img.src = pngDataUrl;
  });
}

function useNarrowViewport(): boolean {
  const [narrow, setNarrow] = useState(() => window.matchMedia('(max-width: 768px)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const onChange = (e: MediaQueryListEvent) => setNarrow(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return narrow;
}

// Cloud sync: snapshot of the local doc at boot (used for the cloud-pull decision, computed once).
// SPEC-467 M1: carries the persisted fingerprint (push-fingerprint algorithm) so boot
// reconciliation can detect unsynced local edits against lastSyncedFingerprint — savedAt no
// longer decides anything.
const boot = (() => {
  const saved = restore();
  return { restored: !!saved, fingerprint: saved ? docFingerprint(saved) : null };
})();

function initState() {
  const saved = restore();
  if (saved) return { ...initialState, projects: saved.projects, currentId: saved.currentId, currentPageId: saved.currentPageId, components: saved.components, deleted: saved.deleted };
  // Brand-new user: seed one project so the brief → poster loop works out of the box after login
  const seed = makeProject(translate(getLang(), 'project.untitled'));
  return { ...initialState, projects: [seed], currentId: seed.id, currentPageId: seed.pages[0].id };
}

export default function App() {
  const [auth, setAuth] = useState<AuthState>('unknown');
  const [expired, setExpired] = useState(false);
  // SPEC-391: session identity (username + role) for the admin surface
  const [me, setMe] = useState<{ username: string; role: string } | null>(null);
  const [adminOpen, setAdminOpen] = useState(false);
  const [state, dispatch] = useReducer(reducer, undefined, initState);
  const [exportError, setExportError] = useState<string | null>(null);
  // Panel collapse/expand: remembered in localStorage; narrow screens force expanded.
  const [chatCollapsed, setChatCollapsed] = useState(() => { try { return localStorage.getItem('design-chat-collapsed') === '1'; } catch { return false; } });
  const [sideCollapsed, setSideCollapsed] = useState(() => { try { return localStorage.getItem('design-side-collapsed') === '1'; } catch { return false; } });
  // Props-panel collapsed state — shrinks to a small bar when it blocks the canvas.
  // Default-collapsed is a BEHAVIOR, not a preference (SPEC-374, correcting SPEC-366): every new single-object
  // selection opens the panel collapsed (see onSelect below); a manual expand only lasts for the current selection.
  // The old design persisted the toggle in localStorage after a one-time migration — one expand (or a stale pre-migration
  // tab) flipped the "default" forever. Session-scoped now; no storage keys on purpose.
  const [propsCollapsed, setPropsCollapsed] = useState(true);
  useEffect(() => { try { localStorage.setItem('design-chat-collapsed', chatCollapsed ? '1' : '0'); } catch { /* ignore */ } }, [chatCollapsed]);
  useEffect(() => { try { localStorage.setItem('design-side-collapsed', sideCollapsed ? '1' : '0'); } catch { /* ignore */ } }, [sideCollapsed]);
  const narrow = useNarrowViewport();
  // Region-select local editing: regionTarget = target of selection mode; regionPending = region chosen, awaiting instruction.
  // Rotated objects don't support region select (crop coordinate systems wouldn't line up); the entry point is hidden for them.
  // regionPending also stores relRect (canvas coords relative to the object) — used to anchor the instruction bar near the selection.
  const [regionTarget, setRegionTarget] = useState<string | null>(null);
  const [regionPending, setRegionPending] = useState<{ objectId: string; relRect: RegionRect } | null>(null);
  const [regionBusy, setRegionBusy] = useState(false);
  const [regionError, setRegionError] = useState<string | null>(null);
  // Transient region hint (.impeccable.md §5): shown when a region session starts, auto-fades after 3s so the
  // bubble never occludes the instruction bar; hidden immediately once the composer is open (selection made).
  const [hintVisible, setHintVisible] = useState(false);
  useEffect(() => {
    if (!regionTarget || regionPending) { setHintVisible(false); return; }
    setHintVisible(true);
    const id = window.setTimeout(() => setHintVisible(false), 3000);
    return () => window.clearTimeout(id);
  }, [regionTarget, regionPending]);
  // Mobile: narrow screens use a single-column tab switch, defaulting to canvas; wide screens ignore this state via CSS.
  const [mobileTab, setMobileTab] = useState<'chat' | 'canvas' | 'layers'>('canvas');
  // SPEC-468 M1: topbar overflow menu — mobile-only surface (the desktop wrapper is
  // display:contents, so desktop rendering is pixel-identical). Render-layer UI state;
  // never touches boot/push/pull paths (G3).
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e: PointerEvent) => {
      const el = e.target as HTMLElement | null;
      if (el && !el.closest('.topbar-actions') && !el.closest('.topbar-menu-btn')) setMenuOpen(false);
    };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, [menuOpen]);
  // Preview mode: clicking an object with a navigation link switches pages; nothing is editable; exit via Esc or the topbar button.
  const [preview, setPreview] = useState(false);
  const stageRef = useRef<Konva.Stage | null>(null);
  const canvasAreaRef = useRef<HTMLElement | null>(null); // coordinate reference for anchoring the region instruction bar
  const pollersRef = useRef<Record<string, () => void>>({});
  const t = useT();
  const theme = useTheme();

  const project = currentProject(state);
  const page = currentPage(state);

  // ===== Session =====
  useEffect(() => {
    let alive = true;
    void fetchMe().then(m => { if (alive) { setAuth(m ? 'authed' : 'anon'); setMe(m); } });
    onUnauthorized(() => { setExpired(true); setAuth('anon'); });
    return () => { alive = false; };
  }, []);

  useEffect(() => persist(state), [state]);

  // ===== Cross-device cloud sync (SPEC-467: cloud-first) =====
  // After login, pull the cloud doc (three-state, SPEC-454 M1): the server doc is the source of
  // truth. No local archive / local synced → adopt the cloud unconditionally; unsynced local
  // edits → push-merge through the server union merge, then converge on the server result.
  // Nothing is pushed to the cloud until the decision completes (G3), so a new device's seed
  // project can't overwrite real data.
  const [cloudReady, setCloudReady] = useState(false);
  // SPEC-454 M1: a failed pull ≠ an empty cloud — show a light "cloud sync unavailable" hint and retry
  // with backoff (5s/15s/45s… cap 120s). cloudReady stays false while retrying, so the push effects
  // below never fire; local editing + localStorage persistence continue untouched.
  // SPEC-467 M2: cloudPaused generalizes to syncStatus — 'paused' keeps the SPEC-454 toast
  // verbatim (G1), 'syncing'/'retrying' drive the lightweight push indicator, 'synced' renders
  // nothing (success path stays zero-distraction, G5).
  const [syncStatus, setSyncStatus] = useState<SyncStatus>('synced');
  useEffect(() => {
    if (auth !== 'authed' || cloudReady) return;
    let alive = true;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let wake: (() => void) | null = null;
    const sleep = (ms: number) => new Promise<void>(resolve => {
      wake = resolve;
      timer = setTimeout(() => { wake = null; resolve(); }, ms);
    });
    void pullDocWithRetry({
      fetchOnce: fetchDoc,
      sleep,
      alive: () => alive,
      onError: () => { if (alive) setSyncStatus('paused'); },
    }).then(raw => {
      if (!alive || !raw) return; // unmounted / logged out mid-retry → never mark ready
      setSyncStatus('synced');
      // SPEC-467 M1: cloud-first boot reconciliation (replaces the whole-doc savedAt LWW).
      // The cloudReady/push gate opens only after the decision completes (G3).
      void runBootReconcile({
        pull: raw,
        boot: { restored: boot.restored, fingerprint: boot.fingerprint },
        localDoc: () => planPush(stateRef.current, null)!,
        markPushed: fp => { lastPushedRef.current = fp; }, // PUT 前 rebase，debounce push 路径同语义：守卫 repull 在 boot 才能放行
        lastSynced: {
          read: () => readLastSyncedFingerprint(localStorage),
          write: fp => writeLastSyncedFingerprint(fp, localStorage),
        },
        parseCloud: parsePersistedDoc,
        adopt: cloud => {
          const nextState = {
            ...initialState,
            projects: cloud.projects,
            currentId: cloud.currentId,
            currentPageId: cloud.currentPageId,
            components: cloud.components,
            deleted: cloud.deleted,
          };
          dispatch({ type: 'LOAD', state: nextState });
          // Rebase the push fingerprint: the adopted cloud doc IS the local truth — the next
          // debounce finds it unchanged and skips a redundant push (SPEC-388/G2 hygiene).
          lastPushedRef.current = docFingerprint(nextState);
          return lastPushedRef.current;
        },
        putOnce: docJson => putDoc(docJson),
        repullAfterMerge,
        sleep,
        alive: () => alive,
        onStatus: s => { if (alive) setSyncStatus(s); },
      }).then(done => {
        if (!alive || !done) return;
        setCloudReady(true);
      });
    });
    return () => {
      alive = false;
      if (timer) clearTimeout(timer);
      const w = wake; wake = null; w?.(); // release a pending backoff sleep so the loop exits
    };
  }, [auth, cloudReady]);

  // Push changes to the cloud with a 10s debounce (fail silently: network/session issues must not block design work).
  // SPEC-388 KV hygiene: pushes whose persisted doc is unchanged (savedAt excluded from the fingerprint) are
  // skipped — UI-only state churn (selection/busy/region) no longer burns CF free-tier KV writes. The old 2s
  // debounce on every state change was the account's main write-quota burner during active design sessions.
  const lastPushedRef = useRef<string | null>(null);
  const stateRef = useRef(state);
  stateRef.current = state;

  // SPEC-454 M3 — one re-pull after a merged PUT: GET → LOAD the server's merge result so the UI
  // converges with what was actually written (revoked projects disappear; no unconditional 200
  // masking a server-side rewrite). Guard: LOAD only while the local doc still matches what this
  // push sent (fingerprint unchanged) — clobbering edits made during the round-trip would re-open
  // the very data-loss hole SPEC-454 closes. If local did diverge, skip: the next debounced push
  // re-merges server-side and gets its own merged verdict, so convergence is preserved.
  const repullAfterMerge = async (): Promise<void> => {
    const res = await fetchDoc();
    if (res.status !== 'doc') return; // empty/error → nothing to converge; the push cycle retries
    const cloud = parsePersistedDoc(res.doc);
    if (!cloud) return;
    const current = docFingerprint(stateRef.current);
    if (current !== lastPushedRef.current) return;
    const nextState = {
      ...initialState,
      projects: cloud.projects,
      currentId: cloud.currentId,
      currentPageId: cloud.currentPageId,
      components: cloud.components,
      deleted: cloud.deleted,
    };
    // Re-base the push fingerprint on the loaded doc: the server result IS the new local truth,
    // so the next debounce finds it unchanged and skips a redundant push (SPEC-388 hygiene).
    // SPEC-467 M1: lastSyncedFingerprint rebases with it — the converged doc counts as synced.
    lastPushedRef.current = docFingerprint(nextState);
    writeLastSyncedFingerprint(lastPushedRef.current, localStorage);
    dispatch({ type: 'LOAD', state: nextState });
  };

  // SPEC-467 M2: serialized push queue — putDoc failures are no longer silently dropped.
  // Backoff (retryDelayMs 5s/15s/45s… cap 120s) retries until success or session end; edits
  // made mid-retry replace the queued payload (latest doc wins; the server union-merges per
  // project anyway). Success keeps SPEC-454 M3's merged→repull convergence and records
  // lastSyncedFingerprint (SPEC-467 M1). 401 semantics untouched: putDoc resolves ok:false
  // while apiFetch fires onUnauthorized → login gate (G7).
  const authRef = useRef(auth);
  authRef.current = auth;
  const mountedRef = useRef(true);
  // StrictMode dev double-mount: the first cleanup flips the ref false and setup must re-arm
  // it, otherwise alive() stays false forever in dev and pushes run silently (prod unaffected).
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);
  const pushQueueRef = useRef<{ docJson: string; fingerprint: string } | null>(null);
  const pushRunningRef = useRef(false);
  const startPushLoop = () => {
    if (pushRunningRef.current) return;
    pushRunningRef.current = true;
    let sent: { docJson: string; fingerprint: string } | null = null;
    setSyncStatus('syncing');
    void putDocWithRetry({
      putOnce: () => { const item = pushQueueRef.current!; sent = item; return putDoc(item.docJson); },
      sleep,
      alive: () => mountedRef.current && authRef.current === 'authed',
      onRetry: () => setSyncStatus('retrying'),
      onGateRejected: () => setSyncStatus('rejected'),
    }).then(async res => {
      pushRunningRef.current = false;
      if (!res || !sent) return; // unmounted / logged out mid-retry
      // SPEC-469 M2: 确定性 409 拒绝 — onGateRejected 已置 'rejected'；不写 lastSynced、不 repull、
      // 不标 synced。pushRunningRef 已复位 ⇒ 下一次本地编辑 enqueuePush 立即重新武装重推
      // （补完审查后即可过门），无永久卡死 flag。
      if (!res.ok) return;
      if (pushQueueRef.current === sent) pushQueueRef.current = null;
      writeLastSyncedFingerprint(sent.fingerprint, localStorage);
      // merged → repull only when nothing newer is queued: a pending payload gets its own
      // merged verdict (the repull fingerprint guard would race a mid-flight replacement).
      if (res.merged && !pushQueueRef.current) await repullAfterMerge();
      if (pushQueueRef.current) startPushLoop();
      else setSyncStatus('synced');
    });
  };
  const enqueuePush = (item: { docJson: string; fingerprint: string }) => {
    pushQueueRef.current = item;
    if (!pushRunningRef.current) startPushLoop();
  };

  useEffect(() => {
    if (auth !== 'authed' || !cloudReady) return;
    const timer = setTimeout(() => {
      // SPEC-388/G2 (unchanged): persisted fingerprint identical → skip; pure UI-state churn
      // (selection/busy/region) never reaches the network.
      const plan = planPush(state, lastPushedRef.current);
      if (!plan) return;
      lastPushedRef.current = plan.fingerprint;
      enqueuePush(plan);
    }, 10_000);
    return () => clearTimeout(timer);
  }, [state, auth, cloudReady]);

  // Best-effort flush when the tab closes, so the longer debounce doesn't widen the data-loss window.
  useEffect(() => {
    const flush = () => {
      if (auth !== 'authed' || !cloudReady) return;
      const plan = planPush(stateRef.current, lastPushedRef.current);
      if (!plan) return;
      lastPushedRef.current = plan.fingerprint;
      // SPEC-467 M2: best-effort semantics unchanged, but a failed flush now enters the same
      // retry queue; if the page dies before success, lastSyncedFingerprint stays stale and
      // the next boot treats local as unsynced (push-merge) — no silent loss.
      enqueuePush(plan);
    };
    window.addEventListener('pagehide', flush);
    return () => window.removeEventListener('pagehide', flush);
  }, [auth, cloudReady]);

  useEffect(() => () => {
    Object.values(pollersRef.current).forEach(stop => stop());
  }, []);

  useEffect(() => {
    if (!exportError) return;
    const timer = setTimeout(() => setExportError(null), 5000);
    return () => clearTimeout(timer);
  }, [exportError]);

  // ===== Agent interaction =====
  // ===== SPEC-415 T-703 ③: WebP relay compression (lib/imgRelay) =====
  // Every landed KV image >400KB gets re-encoded in-browser to WebP q85 (Safari fallback JPEG q85) and
  // re-uploaded persistent; the store swaps src under a race guard (object still exists + src===oldSrc).
  // Fire-and-forget: relayCompress never throws — null = silent give-up, the original image stays as is.
  const relaySrc = useCallback((objId: string, src: string | undefined) => {
    if (!src || !src.includes('/api/img/')) return;
    void relayCompress(src).then(newSrc => {
      if (newSrc) dispatch({ type: 'IMG_RELAYED', id: objId, oldSrc: src, newSrc });
    });
  }, []);

  /** ops landing path: add_image/set_image carrying a ready src (job-backed images relay on job done instead). */
  const relayOps = useCallback((ops: CanvasOp[]) => {
    for (const op of ops) {
      if ((op.op === 'add_image' || op.op === 'set_image') && op.src) relaySrc(op.id, op.src);
    }
  }, [relaySrc]);

  const watchJob = useCallback((id: string) => {
    if (pollersRef.current[id]) return;
    pollersRef.current[id] = pollJob(id, (job) => {
      dispatch({ type: 'JOB_UPDATE', job });
      if (job.status === 'done' || job.status === 'error') {
        delete pollersRef.current[id];
        // SPEC-461 M3.2: 末次 critique 之后的 JOB_UPDATE 落地 → 一次性终局补审（shouldFinalReaudit 判定）。
        maybeReauditRef.current?.(job);
      }
      // SPEC-415 T-703 ③: done → relay the result image. Collect every object id carrying this job_id
      // (JOB_UPDATE patches them all wherever they live), compress once, dispatch one guarded swap per object.
      // stateRef read is safe: job_id was attached at add_image time, long before done lands.
      if (job.status === 'done' && job.result_image && job.result_image.includes('/api/img/')) {
        const src = job.result_image;
        const ids: string[] = [];
        for (const p of stateRef.current.projects) {
          for (const pg of p.pages) {
            for (const o of pg.canvas?.objects ?? []) {
              if (o.job_id === job.id) ids.push(o.id);
            }
          }
        }
        if (ids.length > 0) {
          void relayCompress(src).then(newSrc => {
            if (newSrc) for (const oid of ids) dispatch({ type: 'IMG_RELAYED', id: oid, oldSrc: src, newSrc });
          });
        }
      }
    });
  }, []);

  // SPEC-396 follow-up (08-29 卡死/闪渲染根治·其二): after load/restore, resume polling for objects still
  // pending. Without this, a reload mid-generation leaves the 「生成中」 overlay up forever — no poller ever
  // reads the job again, so the worker's read-time reap can't fire and the object never resolves.
  // watchJob is idempotent (pollersRef guard); JOB_UPDATE clears pending on done/error, so no re-watch loop.
  useEffect(() => {
    for (const p of state.projects) {
      for (const pg of p.pages) {
        for (const o of pg.canvas?.objects ?? []) {
          if (o.job_id && o.pending) watchJob(o.job_id);
        }
      }
    }
  }, [state.projects, watchJob]);

  // ===== SPEC-392 段B: visual review loop (screenshot → critique → refine state machine) =====
  // Generation token + AbortController = cancel semantics: every new send and every stop click bumps
  // the token and aborts in-flight requests; loop callbacks re-check after each await and drop stale work.
  const loopGenRef = useRef(0);
  const loopAbortRef = useRef<AbortController | null>(null);
  /** Stop button visibility (ChatPanel) — true while a review loop is between/inside its phases. */
  const [loopActive, setLoopActiveState] = useState(false);
  const loopActiveRef = useRef(false);
  // SPEC-461 M3.2: ref 同步镜像 state — 补审触发点在 pollJob 回调里（闭包读不到最新 state），
  // setLoopActive 全调用点走本 wrapper（既有 7 处调用零改动，ref 恒同步）。
  const setLoopActive = useCallback((v: boolean) => { loopActiveRef.current = v; setLoopActiveState(v); }, []);
  // SPEC-461 M3.2（RC3 根治）: 末次审查档案（done 气泡落盘时写入）+ 一次性补审触发 ref。
  // watchJob 定义在前（useCallback([]) 稳定引用），经 ref 触达后定义的补审；判定走
  // agentLoop.shouldFinalReaudit 纯函数（pin 面），reaudited 先置位防并发双触发。
  const lastReviewRef = useRef<LastReviewRecord | null>(null);
  const maybeReauditRef = useRef<((job: { status: string }) => void) | null>(null);

  // ===== SPEC-414 T-b: async turn machinery (submit → poll → terminal; docs/async-agent-turns.md §2/§3) =====
  /** Turn currently in flight server-side — the stop button POSTs /cancel so the worker winds down between rounds instead of burning a zombie turn. */
  const activeTurnRef = useRef<string | null>(null);
  /** Live progress from the poll loop (minimal version: rounds done / jobs enqueued) — consumed by the thinking bubble. */
  const [turnProgress, setTurnProgress] = useState<TurnProgress | null>(null);

  /**
   * Poll a submitted turn to its terminal state and map it onto the AgentResult every caller already consumes.
   * Stop = the loop AbortController (abort → AbortError → silent drop, same semantics as the old sync fetch abort);
   * 600s frontend backstop → confirm keep-waiting / cancel (design §2); cancelled/error terminals throw so the
   * existing AGENT_ERROR catch paths render the retryable bubble.
   */
  const awaitTurn = useCallback(async (turnId: string, ac: AbortController): Promise<AgentResult> => {
    activeTurnRef.current = turnId;
    try {
      for (;;) {
        let rec: TurnRecord;
        try {
          rec = await pollTurn(turnId, { signal: ac.signal, onProgress: setTurnProgress });
        } catch (e: any) {
          if (e?.backstop !== true) throw e; // AbortError / hard failure — the caller's catch handles it
          if (window.confirm(t('chat.turnBackstop'))) continue; // keep waiting — fresh 600s budget
          void cancelTurn(turnId).catch(() => {});
          throw new DOMException('Turn cancelled at poll backstop', 'AbortError');
        }
        if (rec.status === 'done') {
          if (!rec.result) throw new Error('turn finished without a result');
          return rec.result as AgentResult;
        }
        if (rec.status === 'cancelled') throw new DOMException('Turn cancelled', 'AbortError');
        throw new Error(rec.error ?? `turn failed (${rec.error_reason ?? 'unknown'})`);
      }
    } finally {
      if (activeTurnRef.current === turnId) activeTurnRef.current = null;
    }
  }, [t]);

  /**
   * Submit + await one async turn — the single /api/chat path for handleSend and both refine loops.
   * extra.projectId is mandatory server-side (async branch); the SPEC-402 measurement chain rides through untouched.
   * Superseded/stopped between submit and poll → cancel immediately so no zombie turn keeps burning tokens.
   */
  const runTurn = useCallback(async (opts: {
    message: string; canvas: CanvasState; chat: ChatMsg[]; needsName: boolean;
    images?: string[]; extra: SubmitChatExtra; ac: AbortController; alive: () => boolean;
  }): Promise<AgentResult> => {
    setTurnProgress(null); // never show the previous turn's counter while a fresh one is being submitted
    const { turnId } = await submitChat(opts.message, opts.canvas, opts.chat, opts.needsName, opts.images, opts.extra);
    activeTurnRef.current = turnId; // the stop button can cancel from this moment on
    if (!opts.alive()) {
      void cancelTurn(turnId).catch(() => {});
      activeTurnRef.current = null;
      throw new DOMException('Turn superseded before polling', 'AbortError');
    }
    return awaitTurn(turnId, opts.ac);
  }, [awaitTurn]);

  const handleStopLoop = useCallback(() => {
    loopGenRef.current++; // invalidate the running loop's generation
    loopAbortRef.current?.abort();
    loopAbortRef.current = null;
    // SPEC-414 T-b 取消语义升级：abort 只停本地轮询；POST /cancel 让 worker 在轮间检查 flag 干净收尾，
    // 不再白跑到底烧 token（设计稿 §2）。
    const turnId = activeTurnRef.current;
    if (turnId) {
      activeTurnRef.current = null;
      void cancelTurn(turnId).catch(() => {});
    }
    // A stop during an in-flight turn aborts its poll after AGENT_START — release busy,
    // otherwise the composer stays locked forever (the aborted turn never dispatches a result).
    dispatch({ type: 'AGENT_CANCEL' });
    setLoopActive(false);
    setTurnProgress(null);
  }, []);

  // ===== SPEC-414 T-b: reload reattach — a turn in flight survives refresh (SPEC-396 job-resume precedent) =====
  // After auth + cloud-doc settle, look for this user's non-terminal turn and resume polling it; the result lands
  // via the normal AGENT_RESULT path, so a mid-turn refresh no longer loses the round (design §2 「刷新/回访 reattach」).
  // The pre-refresh review loop can't resume (its local state machine is gone) — reattach recovers the turn itself.
  const reattachTriedRef = useRef(false);
  useEffect(() => {
    if (auth !== 'authed' || !cloudReady || !me || reattachTriedRef.current) return;
    reattachTriedRef.current = true;
    void (async () => {
      let turns: TurnRecord[];
      try { turns = await fetchActiveTurns(); } catch { return; }
      // newest-first + 单飞行 guard ⇒ the first match is the user's only in-flight turn
      const rec = turns.find(x => x.user === me.username && (x.status === 'queued' || x.status === 'running'));
      if (!rec) return;
      const st = stateRef.current;
      const proj = st.projects.find(p => p.id === rec.project_id);
      if (!proj) return; // project gone locally — no landing spot for the result
      // TurnRecord carries no page id (T-a contract) — land on that project's current page, else its first page.
      const pg = (st.currentId === proj.id ? proj.pages.find(p => p.id === st.currentPageId) : undefined) ?? proj.pages[0];
      if (!pg) return;
      const gen = ++loopGenRef.current;
      const ac = new AbortController();
      loopAbortRef.current = ac;
      setLoopActive(true);
      dispatch({ type: 'AGENT_START' }); // lock the composer — 单飞行: this turn owns the session
      try {
        const result = await awaitTurn(rec.id, ac);
        if (gen !== loopGenRef.current) return;
        dispatch({ type: 'AGENT_RESULT', projectId: rec.project_id, pageId: pg.id, reply: result.reply, ops: result.ops, jobs: result.jobs, error: result.error, projectTitle: result.projectTitle, plan: result.plan, goal: result.goal });
        result.jobs.forEach(j => watchJob(j.id));
        relayOps(result.ops);
      } catch (e: any) {
        if (gen !== loopGenRef.current || isAbortError(e)) return; // stopped/superseded — handleStopLoop already released busy
        dispatch({ type: 'AGENT_ERROR', error: e?.message || String(e), projectId: rec.project_id });
      } finally {
        if (gen === loopGenRef.current) { setLoopActive(false); setTurnProgress(null); }
      }
    })();
  }, [auth, cloudReady, me, awaitTurn, watchJob, relayOps]);

  /** Live project/page by id from the newest state — async loop steps must never capture stale data. */
  const getView = useCallback((projectId: string, pageId: string) => {
    const st = stateRef.current;
    const proj = st.projects.find(p => p.id === projectId);
    const pg = proj?.pages.find(p => p.id === pageId);
    return proj && pg ? { project: proj, page: pg } : null;
  }, []);

  /**
   * Clean-render capture: selection chrome (Transformer + dashed outline) must not enter the review
   * screenshot — clear selection, let react-konva commit, capture, restore. pixelRatio 1/scale =
   * same precedent as handleExport.
   */
  const captureCleanScreenshot = useCallback(async (): Promise<string | null> => {
    const stage = stageRef.current;
    if (!stage) return null;
    const prevIds = stateRef.current.selectedIds;
    if (prevIds.length > 0) dispatch({ type: 'SELECT', id: null });
    await sleep(SELECTION_SETTLE_MS);
    let dataUrl: string | null = null;
    try {
      const scale = stage.scaleX() || 1;
      dataUrl = stage.toDataURL({ pixelRatio: 1 / scale, mimeType: 'image/png' });
    } catch { dataUrl = null; /* tainted canvas etc. — caller treats as "skip review" */ }
    if (prevIds.length > 0) dispatch({ type: 'SELECT_MANY', ids: prevIds });
    return dataUrl;
  }, []);

  /**
   * Gallery thumbnail (SPEC-404): reuses the review-loop clean capture, then downscales. Identical
   * pixel content yields an identical JPEG, so the PROJECT_THUMB dispatch is skipped when unchanged —
   * no localStorage/cloud churn, and the idle capture below can't retrigger itself.
   */
  const snapCurrentThumb = useCallback(async (): Promise<void> => {
    const st = stateRef.current;
    if (st.view !== 'workbench' || !st.currentId) return;
    const shot = await captureCleanScreenshot();
    if (!shot) return;
    const thumb = await downscaleThumb(shot);
    const cur = stateRef.current.projects.find(p => p.id === st.currentId);
    if (!cur || cur.thumb === thumb) return;
    dispatch({ type: 'PROJECT_THUMB', id: st.currentId, thumb });
  }, [captureCleanScreenshot]);

  // SPEC-404: keep the gallery thumb fresh — capture after 10s idle following a canvas change. Any
  // mutation produces a new state.projects reference, which resets the timer.
  useEffect(() => {
    if (state.view !== 'workbench' || !state.currentId) return;
    const timer = setTimeout(() => { void snapCurrentThumb(); }, 10_000);
    return () => clearTimeout(timer);
  }, [state.projects, state.currentId, state.currentPageId, state.view, snapCurrentThumb]);

  /** Leave the canvas for the gallery: grab a fresh thumb first (the stage unmounts on the view switch). */
  const goHome = useCallback(async (): Promise<void> => {
    await snapCurrentThumb();
    dispatch({ type: 'SET_VIEW', view: 'home' });
  }, [snapCurrentThumb]);

  const openProject = useCallback((id: string): void => {
    dispatch({ type: 'PROJECT_SWITCH', id });
    dispatch({ type: 'SET_VIEW', view: 'workbench' });
  }, []);

  /**
   * The review loop itself (SPEC-392 段B). Each phase is one request; the frontend drives the state
   * machine: wait jobs → clean screenshot → upload → /api/critique → (pass|budget ⇒ done) | (hard
   * issues ⇒ refine round through the normal /api/chat path, then loop). Soft issues are never fixed
   * — they ride along in the done message.
   */
  const runReviewLoop = useCallback(async (opts: {
    gen: number; ac: AbortController; projectId: string; pageId: string;
    instruction: string; jobIds: string[]; draftSummary?: string;
    /** SPEC-426 M2 A.4 (修复轮): 带参考图 → critique 另评还原度维度（forwarded to postCritique） */
    hasReference?: boolean;
    /** SPEC-427 D: insistsNoReference 路径 — done 泡/refine 泡的完整性宣称需 passing critique 证据才保留 */
    noRef?: boolean;
  }): Promise<void> => {
    const { gen, ac, projectId, pageId, instruction } = opts;
    const alive = () => gen === loopGenRef.current && !ac.signal.aborted;
    const steps: ChatProcessStep[] = [];
    const summaries: string[] = [];
    if (opts.draftSummary && opts.draftSummary !== NO_CHANGE_SUMMARY) summaries.push(opts.draftSummary);
    let jobIds = opts.jobIds;
    let softCount = 0;
    let unresolvedHard: CritiqueIssue[] = [];
    // SPEC-427 A: frozen-verdict 熔断命中时携诊断信息落到 done 气泡（frozen 位 + 已试轮数）。
    let frozenInfo: { issues: CritiqueIssue[]; roundsTried: number } | null = null;
    // SPEC-427 B: 上一 refine 轮的 ops diff 是否为空（宣称对账证据之一）。
    let lastRefineEmpty = false;
    // SPEC-462 M4: 收敛再诊断状态 — 单 finding（residualFingerprint）跨轮账本 + 已给过
    // 再归因轮的指纹集（frozen 推迟每指纹一次）+ 已发修复轮的 ops 摘要素材。
    let findingRounds: FindingRounds = new Map();
    const reattributedFps = new Set<string>();
    const triedOpsRounds: { ops: number; jobs: number }[] = [];
    let prevIssues: CritiqueIssue[] = []; // SPEC-426 M2 A.3: cross-round memory for the regression guard
    let lastDimensions: Record<string, number> | undefined; // SPEC-426 M2 A.4: 最新一轮评分 → done 卡片
    let budget = REFINE_BUDGET_VISUAL_DEFAULT; // locked by the first verdict: structural issues → 3, else 2 (visual-phase constants, SPEC-426 M2 A.2)
    let used = 0;
    setLoopActive(true);
    try {
      while (alive()) {
        // 1) Wait for this round's image jobs to settle — the screenshot needs final pixels.
        //    Deadline ≈ pollJob's own 400s cap; on timeout we proceed best-effort.
        const deadline = Date.now() + JOBS_SETTLE_TIMEOUT_MS;
        let settled = jobIds.length === 0;
        while (!settled && alive() && Date.now() < deadline) {
          await sleep(JOBS_SETTLE_POLL_MS);
          if (!alive()) return;
          const jobs = stateRef.current.jobs;
          settled = jobIds.every(id => { const j = jobs[id]; return !!j && (j.status === 'done' || j.status === 'error'); });
        }
        if (!alive()) return;
        await sleep(SETTLE_RENDER_DELAY_MS); // let Konva repaint after the last JOB_UPDATE patch
        if (!alive()) return;

        // 2) Screenshot. User navigated to another project/page mid-loop → drop the loop silently:
        //    the stage now renders a different canvas and we must never critique the wrong one.
        const st = stateRef.current;
        if (st.currentId !== projectId || st.currentPageId !== pageId) return;
        const view = getView(projectId, pageId);
        if (!view) return; // project/page deleted mid-flight
        const shotAt = Date.now();
        const shot = await captureCleanScreenshot();
        if (!alive()) return;
        if (!shot) break; // export failed — finish without review rather than blocking

        let screenshotUrl: string;
        try {
          screenshotUrl = await uploadImage(shot, SCREENSHOT_TTL_DAYS, ac.signal);
        } catch (e: any) {
          if (!alive()) return;
          steps.push({ phase: 'screenshot', ts: shotAt, ms: Date.now() - shotAt, note: `upload error: ${e?.message || String(e)}` });
          break; // review infra unavailable → done, don't block the user
        }
        if (!alive()) return;
        steps.push({ phase: 'screenshot', ts: shotAt, ms: Date.now() - shotAt, screenshotUrl });

        // 3) Critique.
        const cAt = Date.now();
        let verdict: CritiqueResult;
        try {
          verdict = await postCritique(instruction, view.page.canvas, screenshotUrl, ac.signal, opts.hasReference);
        } catch (e: any) {
          if (!alive()) return;
          steps.push({ phase: 'critique', ts: cAt, ms: Date.now() - cAt, screenshotUrl, note: `error: ${e?.message || String(e)}` });
          break;
        }
        if (!alive()) return;
        // SPEC-460 M3-1: 审计/vision 汇总仲裁 — /api/critique 客观数据段 placement 的确定性摆位
        // 审计（hard/soft）与 vision verdict 汇总：任一有 hard 即 fail — 确定性 hard 不可被
        // vision「pass」覆盖（证据 3 误报采信根治）；下游回归守卫/frozen/预算/refine 管线复用 arb.issues。
        const arb = arbitrateCritique(verdict);
        const hard = arb.issues.filter(i => i.severity === 'hard');
        softCount = arb.issues.length - hard.length;
        // SPEC-426 M2 A.3 (修复轮): 收敛与回归守卫 — 与上一轮 verdict 比对，回归标记写进本轮
        // critique step（审查留痕）。首轮无 prev → 不判定、不标记。
        const guard = prevIssues.length > 0 ? detectRegression(prevIssues, arb.issues) : null;
        // SPEC-462 M4: 每轮 verdict 后按单 finding 连续轮数记账（det+vision 双通道同一缺陷
        // 按 residualFingerprint 聚合），stuck = 连续 2 修复轮仍存 → 本轮指令切再归因形态。
        findingRounds = updateFindingRounds(findingRounds, arb.issues);
        const stuck = stuckFindings(findingRounds, arb.issues);
        if (verdict.dimensions) lastDimensions = verdict.dimensions; // A.4: done 卡片展示最后一轮评分
        steps.push({ phase: 'critique', ts: cAt, ms: Date.now() - cAt, screenshotUrl, verdict: { pass: arb.pass, hard: hard.length, soft: softCount }, ...(guard?.regression ? { regression: true } : {}), ...(arb.resolved.length > 0 ? { resolvedClaims: arb.resolved.length } : {}) });

        if (arb.pass || hard.length === 0) break; // pass — or only soft issues (soft rides along hard-triggered refine rounds, never triggers one alone)
        // SPEC-427 B: 宣称对账 — 上一 refine 轮宣称修正但 verdict hard 指纹零变化且该轮
        // ops diff 为空 → 改写已落泡的宣称（RECONCILE_CLAIM）。先于 frozen 判断：两层可同时
        // 命中（宣称改写 + 熔断硬停），与既有 A.1/B.1 剥离规则叠加不冲突。
        if (prevIssues.length > 0 && lastRefineEmpty && sameHardFingerprints(prevIssues, arb.issues)) {
          dispatch({ type: 'RECONCILE_CLAIM', projectId, issues: hard });
        }
        // SPEC-427 A: frozen-verdict 硬熔断 — 连续两轮 critique 的 hard 指纹集合完全一致 →
        // 文字修正已证明无效：refine 循环硬停（不再消耗预算、不再发修正轮），done 气泡携
        // 诊断（frozen 清单/已试轮数/建议动作），「继续修正」按钮对 frozen 清单禁用。
        if (prevIssues.length > 0 && isVerdictFrozen(prevIssues, arb.issues)
          // SPEC-462 M4 时序红线：再诊断先于熔断生效 — frozen 命中的 verdict 仍含首次再归因
          // 的 stuck finding 时先发一轮再归因修复轮（reattributedFps 每指纹一次），下一轮仍
          // 冻结再照走熔断（SPEC-427 语义不变；预算检查原位不绕过 — 结构类 desc 含溢出/
          // 重叠关键词 → budget=3，E2/E5 族的再归因轮在预算内）。
          && !reattributionDefersFreeze(stuck, reattributedFps)) {
          frozenInfo = { issues: hard, roundsTried: used };
          unresolvedHard = hard;
          break;
        }
        if (used === 0) budget = refineBudget(arb.issues, 'visual');
        if (used >= budget) { unresolvedHard = hard; break; } // budget exhausted → done with a note

        // 4) Refine: hard issues (plus soft ones tagged secondary) go back to the planner as a
        //    correction turn on the normal /api/chat path (existing nudge/action intercepts still
        //    apply), then the loop re-screenshots.
        used++;
        prevIssues = arb.issues; // A.3: this verdict becomes the next round's prev
        const rAt = Date.now();
        // SPEC-462 M4: stuck finding（连续 2 修复轮仍存）→ 本轮指令切再归因形态（历史轮
        // desc + 已试 ops 摘要 + 换根因/降级要求）；无 stuck → 旧 ctx 形态（byte-identical）。
        let reattribute: ReattributionInfo[] | undefined;
        if (stuck.length > 0) {
          const triedOps = summarizeTriedOps(triedOpsRounds);
          reattribute = stuck.map(s => ({ issue: s.issue, history: s.descs, triedOps }));
          for (const s of stuck) reattributedFps.add(s.fingerprint);
        }
        const refineCtx = guard || reattribute
          ? { ...(guard ? { fixed: guard.fixed, regressed: guard.regressed } : {}), ...(reattribute ? { reattribute } : {}) }
          : undefined;
        const refineMsg = buildRefineInstruction(arb.issues, refineCtx);
        const v2 = getView(projectId, pageId);
        if (!v2) return;
        dispatch({ type: 'AGENT_START' });
        let r2: AgentResult;
        try {
          r2 = await runTurn({ message: refineMsg, canvas: v2.page.canvas, chat: v2.project.chat, needsName: false, extra: { projectId }, ac, alive });
        } catch (e: any) {
          if (!alive()) return;
          steps.push({ phase: 'refine', ts: rAt, ms: Date.now() - rAt, note: `error: ${e?.message || String(e)}` });
          dispatch({ type: 'AGENT_ERROR', error: e?.message || String(e), retryText: refineMsg, projectId });
          return; // the retryable error bubble is the last word; no done bubble
        }
        if (!alive()) return;
        dispatch({ type: 'AGENT_RESULT', projectId, pageId, reply: r2.reply, ops: r2.ops, jobs: r2.jobs, error: r2.error, noReference: opts.noRef });
        relayOps(r2.ops);
        steps.push({ phase: 'refine', ts: rAt, ms: Date.now() - rAt, ...(r2.error ? { note: `error: ${r2.error}` } : {}) });
        if (r2.error) return; // AGENT_RESULT above already rendered the retryable error bubble
        lastRefineEmpty = r2.ops.length === 0 && r2.jobs.length === 0; // SPEC-427 B: ops diff 证据（图像任务也算变更，保守口径）
        triedOpsRounds.push({ ops: r2.ops.length, jobs: r2.jobs.length }); // SPEC-462 M4: 已试 ops 素材（再归因摘要用）
        r2.jobs.forEach(j => watchJob(j.id));
        if (r2.fact_summary && r2.fact_summary !== NO_CHANGE_SUMMARY) summaries.push(r2.fact_summary);
        jobIds = r2.jobs.map(j => j.id);
      }

      if (!alive()) return;
      // 5) Done: 「生成完毕」 + fact-derived summary (worker buildFactSummary, per turn) + hints.
      //    Process steps attach to this bubble — segment C's collapsible UI consumes them.
      // SPEC-427 A: frozen 诊断优先于预算耗尽文案（两者互斥：frozen 在预算检查前 break）。
      // SPEC-460 M3-3: 预算耗尽仍有 hard（非 frozen）→ 禁以「生成完毕」完成态表述领头——
      // 诚实 lead「N 项未解决」（逐项清单随 residual 落盘，ChatPanel 渲染清单 + 「继续修正」
      // 按钮）；diff loop 的 loopBudgetLeft 文案不受本改动影响。
      const honestLead = !frozenInfo && unresolvedHard.length > 0;
      // SPEC-426 M2 A.3 (修复轮): 预算耗尽仍有 hard → 残留问题清单随 done 气泡落盘。
      // SPEC-463 M2.3: residual 先按 finding 指纹去重（buildResidual 内 dedupHardIssues）—
      // exhausted 计数 = 去重后清单长度（计数与清单一致，双通道同源重复不再虚高）。
      const residual = unresolvedHard.length > 0 ? buildResidual(unresolvedHard) : undefined;
      let text = honestLead ? t('chat.loopUnresolved', { n: residual!.length }) : t('chat.loopDone');
      if (summaries.length > 0) text += `。${summaries.join('；')}`;
      if (frozenInfo) text += t('chat.frozenDiag', { n: frozenInfo.roundsTried });
      else if (softCount > 0 && !honestLead) text += t('chat.loopSoftLeft', { n: softCount });
      // SPEC-427 A.1: the done text must match the FINAL canvas — placeholder-generation
      // claims survive only while placeholders actually remain. The process log stays untouched.
      // SPEC-427 D: noReference 路径的 done 泡同样过完整性层（无 passing critique 即剥）。
      const finalView = getView(projectId, pageId);
      dispatch({ type: 'CHAT_APPEND', projectId, text: buildDoneText(finalView?.page.canvas ?? null, text, steps, opts.noRef ? { noReference: true } : undefined), process: steps, ...(residual ? { residual } : {}), ...(lastDimensions ? { scores: lastDimensions } : {}), ...(frozenInfo ? { frozen: true } : {}) });
      // SPEC-461 M3.2: 末次审查档案 — 此后落地的异步图像任务（JOB_UPDATE done/error）触发
      // 一次性终局补审（screenshot+critique+verdict，不新开修复预算）。
      lastReviewRef.current = { projectId, pageId, instruction, ...(opts.hasReference ? { hasReference: true } : {}), reaudited: false };
    } finally {
      // A superseded loop's finally must not stomp the successor's loopActive flag.
      if (alive()) setLoopActive(false);
    }
  }, [getView, captureCleanScreenshot, watchJob, relayOps, t, runTurn]);

  /**
   * SPEC-461 M3.2（RC3 根治）— 异步落地补审：末次 critique 之后 JOB_UPDATE 落地 → 一次性
   * 终局 screenshot+critique+verdict（不新开修复预算；fail → chat.loopUnresolved 诚实文案 +
   * residual 清单，不开新修复轮，防无限尾巴；pass → 零消息，终态已过审查仅留日志）。任何
   * 失败路径静默放弃（imgRelay 同纪律：补审是诚实层，绝不 throw）。取证：doc-pmurx39zf1kfk
   * 终态存档从未过 screenshot+critique（JOB_UPDATE 13:07 落地 = 末次审查 13:01-13:03 之后）。
   */
  const runFinalReaudit = useCallback(async (review: LastReviewRecord) => {
    const myGen = loopGenRef.current; // 新 send/stop 会 bump gen → 补审让位
    const alive = () => myGen === loopGenRef.current && !loopActiveRef.current;
    try {
      // 等在飞图像任务全部落地（review loop settle 同款节奏；一次性预算，超时尽力而为）。
      const deadline = Date.now() + JOBS_SETTLE_TIMEOUT_MS;
      let settled = false;
      while (!settled && alive() && Date.now() < deadline) {
        const jobs = stateRef.current.jobs;
        settled = Object.values(jobs).every(j => j.status !== 'queued' && j.status !== 'running');
        if (!settled) await sleep(JOBS_SETTLE_POLL_MS);
      }
      if (!alive()) return;
      await sleep(SETTLE_RENDER_DELAY_MS); // let Konva repaint the fresh pixels
      if (!alive()) return;
      const st = stateRef.current;
      if (st.currentId !== review.projectId || st.currentPageId !== review.pageId) return; // 切走 → 静默放弃
      const view = getView(review.projectId, review.pageId);
      if (!view) return; // project/page deleted mid-flight
      const steps: ChatProcessStep[] = [];
      const shotAt = Date.now();
      const shot = await captureCleanScreenshot();
      if (!alive() || !shot) return;
      let screenshotUrl: string;
      try {
        screenshotUrl = await uploadImage(shot, SCREENSHOT_TTL_DAYS);
      } catch { return; } // 审查基建不可用 → 静默放弃（不劣于现状）
      if (!alive()) return;
      steps.push({ phase: 'screenshot', ts: shotAt, ms: Date.now() - shotAt, screenshotUrl });
      const cAt = Date.now();
      let verdict: CritiqueResult;
      try {
        verdict = await postCritique(review.instruction, view.page.canvas, screenshotUrl, undefined, review.hasReference);
      } catch { return; }
      if (!alive()) return;
      const arb = arbitrateCritique(verdict);
      const hard = arb.issues.filter(i => i.severity === 'hard');
      steps.push({ phase: 'critique', ts: cAt, ms: Date.now() - cAt, screenshotUrl, verdict: { pass: arb.pass, hard: hard.length, soft: arb.issues.length - hard.length }, ...(arb.resolved.length > 0 ? { resolvedClaims: arb.resolved.length } : {}) });
      if (arb.pass || hard.length === 0) {
        console.log('[final-reaudit] pass — 终态已过审查，零修复轮', JSON.stringify({ soft: arb.issues.length - hard.length }));
        return; // pass → 零消息（spec 只定义 fail 分支；不新开修复预算）
      }
      // SPEC-463 M2.2（E2 根治）: fail → 单一终局陈述（复用 chat.loopUnresolved 键 — 别造新键）—
      // 最近一条 assistant 泡已披露 exhausted 时合并改写原泡（REAUDIT_MERGE：exhausted 文案
      // 至多 ×1、计数=合并去重后唯一 finding 数、lead 之前误混入的 gate 文案随改写脱落）；
      // 无 exhausted 前提才落首条诚实泡（CHAT_APPEND，residual 同样按指纹去重）。
      const projNow = stateRef.current.projects.find(p => p.id === review.projectId);
      const lastMsg = projNow?.chat[projNow.chat.length - 1];
      const prevBubble = lastMsg && lastMsg.role === 'assistant' && !lastMsg.error ? lastMsg : null;
      const report = mergeReauditReport(prevBubble, hard, n => t('chat.loopUnresolved', { n }));
      if (report.merged) {
        dispatch({ type: 'REAUDIT_MERGE', projectId: review.projectId, text: report.text, process: steps, residual: report.residual, ...(verdict.dimensions ? { scores: verdict.dimensions } : {}) });
      } else {
        dispatch({ type: 'CHAT_APPEND', projectId: review.projectId, text: report.text, process: steps, residual: report.residual, ...(verdict.dimensions ? { scores: verdict.dimensions } : {}) });
      }
    } catch { /* 静默放弃 — 绝不 throw 到调用方 */ }
  }, [getView, captureCleanScreenshot, dispatch, t]);
  // SPEC-461 M3.2: 触发接线 — shouldFinalReaudit 纯函数判定；一次性守卫（reaudited）先置位防并发双触发。
  maybeReauditRef.current = (job) => {
    const review = lastReviewRef.current;
    if (!review) return;
    if (!shouldFinalReaudit({ jobStatus: job.status, loopActive: loopActiveRef.current, lastReview: review, current: { projectId: stateRef.current.currentId, pageId: stateRef.current.currentPageId } })) return;
    review.reaudited = true;
    void runFinalReaudit(review);
  };

  /**
   * SPEC-402 M3 — replication diff loop. Replicate turns (attached reference image + replicate
   * intent) are checked against the reference with an objective diff instead of the free-form
   * critique: wait jobs → clean screenshot → upload → extract render palette → POST
   * /api/replicate-diff → (stop|pass ⇒ done) | (fail ⇒ refine_instruction back through the normal
   * /api/chat path, then loop). The round budget is owned by the worker's `stop` signal
   * (round ≥ 3, or no-hard + both metrics met) — the client never budgets on its own.
   * A 502 (gateway failure) is retried once; a second failure ends the loop with an honest note.
   */
  const runDiffLoop = useCallback(async (opts: {
    gen: number; ac: AbortController; projectId: string; pageId: string;
    referenceImage: string; jobIds: string[]; draftSummary?: string;
  }): Promise<void> => {
    const { gen, ac, projectId, pageId, referenceImage } = opts;
    const alive = () => gen === loopGenRef.current && !ac.signal.aborted;
    const steps: ChatProcessStep[] = [];
    const summaries: string[] = [];
    if (opts.draftSummary && opts.draftSummary !== NO_CHANGE_SUMMARY) summaries.push(opts.draftSummary);
    let jobIds = opts.jobIds;
    let refineRound = 0;
    let diffDown = false; // diff infra failed even after the retry → done-message note
    let exitNote: string | null = null; // hard issues left when `stop` fires → done-message hint
    setLoopActive(true);
    try {
      while (alive()) {
        // 1) Wait for this round's image jobs to settle — the screenshot needs final pixels
        //    (same mechanism/deadlines as the review loop).
        const deadline = Date.now() + JOBS_SETTLE_TIMEOUT_MS;
        let settled = jobIds.length === 0;
        while (!settled && alive() && Date.now() < deadline) {
          await sleep(JOBS_SETTLE_POLL_MS);
          if (!alive()) return;
          const jobs = stateRef.current.jobs;
          settled = jobIds.every(id => { const j = jobs[id]; return !!j && (j.status === 'done' || j.status === 'error'); });
        }
        if (!alive()) return;
        await sleep(SETTLE_RENDER_DELAY_MS); // let Konva repaint after the last JOB_UPDATE patch
        if (!alive()) return;

        // 2) Screenshot + upload. Mid-loop project/page switch → drop silently (never diff the wrong canvas).
        const st = stateRef.current;
        if (st.currentId !== projectId || st.currentPageId !== pageId) return;
        const view = getView(projectId, pageId);
        if (!view) return; // project/page deleted mid-flight
        const shotAt = Date.now();
        const shot = await captureCleanScreenshot();
        if (!alive()) return;
        if (!shot) break; // export failed — finish without diff rather than blocking

        let screenshotUrl: string;
        try {
          screenshotUrl = await uploadImage(shot, SCREENSHOT_TTL_DAYS, ac.signal);
        } catch (e: any) {
          if (!alive()) return;
          steps.push({ phase: 'screenshot', ts: shotAt, ms: Date.now() - shotAt, note: `upload error: ${e?.message || String(e)}` });
          break; // review infra unavailable → done, don't block the user
        }
        if (!alive()) return;
        steps.push({ phase: 'screenshot', ts: shotAt, ms: Date.now() - shotAt, screenshotUrl });

        // 3) Render palette: deterministic pixel sampling of the screenshot itself
        //    (failure → null → worker treats palette as not comparable, never blocks).
        const renderPalette = await extractPaletteFromUrl(screenshotUrl);
        if (!alive()) return;

        // 4) Diff. 502 = gateway failure → retry once; any final failure ends the loop honestly.
        const dAt = Date.now();
        const diffArgs = {
          referenceImage, screenshotUrl, canvas: view.page.canvas, projectId, refineRound,
          ...(renderPalette ? { renderPalette: renderPalette.palette } : {}),
        };
        let diff: ReplicateDiffResult;
        try {
          try {
            diff = await postReplicateDiff(diffArgs, ac.signal);
          } catch (e: any) {
            if (e?.status !== 502 || !alive()) throw e;
            diff = await postReplicateDiff(diffArgs, ac.signal); // single retry on 502
          }
        } catch (e: any) {
          if (!alive()) return;
          diffDown = e?.status === 502;
          steps.push({ phase: 'diff', ts: dAt, ms: Date.now() - dAt, screenshotUrl, note: `error: ${e?.message || String(e)}` });
          break;
        }
        if (!alive()) return;
        const hardCount = diff.issues.filter(i => i.severity === 'hard').length;
        steps.push({
          phase: 'diff', ts: dAt, ms: Date.now() - dAt, screenshotUrl,
          verdict: { pass: diff.pass, hard: hardCount, soft: diff.issues.length - hardCount },
          metrics: diff.metrics,
        });

        // 5) `stop` = worker's objective stop signal; empty refine_instruction = nothing to fix.
        if (diff.stop || diff.pass || !diff.refine_instruction) {
          // Budget/stop fired with hard issues still open → tell the user (review-loop parity).
          // (Soft issues ride in the refine_instruction throughout, so no separate soft note here.)
          if (!diff.pass && hardCount > 0) exitNote = t('chat.loopBudgetLeft', { n: hardCount });
          break;
        }

        // 6) Refine round: the worker's instruction goes back to the planner as a normal chat turn.
        refineRound++;
        const rAt = Date.now();
        const v2 = getView(projectId, pageId);
        if (!v2) return;
        dispatch({ type: 'AGENT_START' });
        let r2: AgentResult;
        try {
          r2 = await runTurn({ message: diff.refine_instruction, canvas: v2.page.canvas, chat: v2.project.chat, needsName: false, extra: { projectId }, ac, alive });
        } catch (e: any) {
          if (!alive()) return;
          steps.push({ phase: 'refine', ts: rAt, ms: Date.now() - rAt, note: `error: ${e?.message || String(e)}` });
          dispatch({ type: 'AGENT_ERROR', error: e?.message || String(e), retryText: diff.refine_instruction, projectId });
          return; // the retryable error bubble is the last word; no done bubble
        }
        if (!alive()) return;
        dispatch({ type: 'AGENT_RESULT', projectId, pageId, reply: r2.reply, ops: r2.ops, jobs: r2.jobs, error: r2.error });
        relayOps(r2.ops);
        steps.push({ phase: 'refine', ts: rAt, ms: Date.now() - rAt, ...(r2.error ? { note: `error: ${r2.error}` } : {}) });
        if (r2.error) return; // AGENT_RESULT above already rendered the retryable error bubble
        r2.jobs.forEach(j => watchJob(j.id));
        if (r2.fact_summary && r2.fact_summary !== NO_CHANGE_SUMMARY) summaries.push(r2.fact_summary);
        jobIds = r2.jobs.map(j => j.id);
      }

      if (!alive()) return;
      let text = t('chat.loopDone');
      if (summaries.length > 0) text += `。${summaries.join('；')}`;
      if (exitNote) text += exitNote;
      if (diffDown) text += t('chat.loopDiffDown');
      // SPEC-426 M2 A.1: same final-canvas consistency filter as the review loop's done bubble
      // (the diff loop logs no critique phase → 自检-style claims never survive here).
      const finalView = getView(projectId, pageId);
      dispatch({ type: 'CHAT_APPEND', projectId, text: buildDoneText(finalView?.page.canvas ?? null, text, steps), process: steps });
    } finally {
      // A superseded loop's finally must not stomp the successor's loopActive flag.
      if (alive()) setLoopActive(false);
    }
  }, [getView, captureCleanScreenshot, watchJob, relayOps, t, runTurn]);

  const handleSend = useCallback(async (text: string, images: string[] = [], isRetry = false, replan?: 'mutate') => {
    if (!project || !page) return;
    // Lock in the project/page at send time — async results must land back on the sender,
    // not on whatever project is current when the result arrives (deleting/switching projects mid-flight would leak objects into another project).
    const projectId = project.id;
    const pageId = page.id;
    // SPEC-392 段B (cancel): a new send supersedes any in-flight review loop — bump the generation
    // token and abort its requests. The old loop's callbacks re-check the token after every await
    // and drop stale responses instead of writing into the new conversation.
    const gen = ++loopGenRef.current;
    loopAbortRef.current?.abort();
    const ac = new AbortController();
    loopAbortRef.current = ac;

    if (!isRetry) dispatch({ type: 'USER_MSG', text, images }); // retries don't duplicate the user bubble
    dispatch({ type: 'AGENT_START' });
    // SPEC-402 M1 replication entry: attached image + (replicate intent OR near-empty canvas).
    // Measure before asking: deterministic palette from pixel sampling, and on empty/new canvases
    // snap the artboard to the nearest preset of the reference image's aspect ratio. Any failure
    // degrades to the pre-M1 path (no palette, no resize) — never blocks the turn.
    let sendCanvas = page.canvas;
    let measuredPalette: { hex: string; coverage: number }[] | undefined;
    let measuredSource: { w: number; h: number } | undefined;
    if (images.length > 0 && (isReplicateIntent(text) || isNearEmptyCanvas(page.canvas))) {
      const measured = await extractPaletteFromUrl(images[0]);
      if (gen !== loopGenRef.current) return; // superseded while measuring — drop silently
      if (measured) {
        measuredPalette = measured.palette;
        measuredSource = { w: measured.w, h: measured.h };
        if (isNearEmptyCanvas(page.canvas)) {
          const preset = snapArtboardPreset(measured.w, measured.h);
          if (preset.w !== page.canvas.artboard.w || preset.h !== page.canvas.artboard.h) {
            dispatch({ type: 'CANVAS_RESIZE', w: preset.w, h: preset.h });
            sendCanvas = { ...page.canvas, artboard: { ...page.canvas.artboard, w: preset.w, h: preset.h } };
          }
        }
      }
    }
    const canvasBefore = sendCanvas; // pre-turn snapshot: intent gate diffs it against the post-ops canvas
    let result: AgentResult;
    try {
      result = await runTurn({
        message: text, canvas: sendCanvas, chat: project.chat, needsName: isDefaultProjectName(project.name),
        images, extra: { projectId: project.id, palette: measuredPalette, source: measuredSource, ...(replan ? { replan } : {}) },
        ac, alive: () => gen === loopGenRef.current,
      });
    } catch (e: any) {
      if (gen !== loopGenRef.current || isAbortError(e)) return; // superseded/stopped — no bubble, no state writes
      // 409 = another turn is still in flight (double tab / double click) — tell the user to stop it first.
      const msg = e?.status === 409 ? t('chat.turnConflict') : (e?.message || String(e));
      dispatch({ type: 'AGENT_ERROR', error: msg, retryText: text, projectId });
      return;
    }
    if (gen !== loopGenRef.current) return; // stale response — drop silently
    // SPEC-427 D: insistsNoReference 路径标记 — 随 AGENT_RESULT 下发，store 剥无据完整性宣称。
    const noRefPath = isNoRefReplicaTurn(text, images.length > 0);
    dispatch({ type: 'AGENT_RESULT', projectId, pageId, reply: result.reply, ops: result.ops, jobs: result.jobs, error: result.error, retryText: text, projectTitle: result.projectTitle, plan: result.plan, goal: result.goal, noReference: noRefPath });
    result.jobs.forEach(j => watchJob(j.id));
    relayOps(result.ops);
    if (result.error) return;

    // Intent gate (SPEC-392 段B): questions / small edits / no-change turns stay 直通 — exactly today's
    // behavior, no /api/critique. New designs and big reworks enter the review phase below.
    // SPEC-426 M2 A.2 (修复轮): grounding = 复刻+带附图，由本分支自行标注（SPEC-402 客观 diff
    // loop，不经 requireReviewPhase）；其余按 isBigChange/ops 判定 — big-change 轮 'visual' 必须
    // 落盘 screenshot+critique 相位，小改轮 'skip' = 审查非强制（gate heuristics 继续生效）。
    const canvasAfter = applyOps(sendCanvas, result.ops);
    const phase: ReviewPhase = images.length > 0 && isReplicateIntent(text)
      ? 'grounding'
      : requireReviewPhase(isBigChange(canvasBefore, canvasAfter, result), result.ops);
    if (!shouldEnterLoop(text, canvasBefore, canvasAfter, result, phase)) return;

    if (phase === 'grounding') {
      void runDiffLoop({ gen, ac, projectId, pageId, referenceImage: images[0], jobIds: result.jobs.map(j => j.id), draftSummary: result.fact_summary });
      return;
    }

    void runReviewLoop({ gen, ac, projectId, pageId, instruction: text, jobIds: result.jobs.map(j => j.id), draftSummary: result.fact_summary, hasReference: images.length > 0, noRef: noRefPath });
  }, [project, page, watchJob, relayOps, runReviewLoop, runDiffLoop, runTurn, t]);

  const handleRetryChat = useCallback((text: string) => {
    void handleSend(text, [], true);
  }, [handleSend]);

  /** SPEC-426 M2 A.3 (修复轮): one-click 继续修正 from the done bubble's residual list — a fresh
   * refine turn with the residual issues, then re-enter the review loop (per-call budget reset =
   * 预算重置一次；循环期间 busy 锁定 + 新 done 气泡覆盖/清空旧 residual = 用后清空防重复触发). */
  const handleContinueRefine = useCallback(async (residual: CritiqueIssue[], frozen?: boolean) => {
    if (!project || !page || residual.length === 0) return;
    const projectId = project.id;
    const pageId = page.id;
    // SPEC-427 A: frozen 清单不接受相同修正再提交 — 直接拒绝并复述诊断。
    // 恢复路径只认「换策略」的显式用户动作（重建 section / 提供参考图 / 新需求消息）。
    if (frozen) {
      dispatch({ type: 'CHAT_APPEND', projectId, text: t('chat.frozenRejected') });
      return;
    }
    const gen = ++loopGenRef.current;
    loopAbortRef.current?.abort();
    const ac = new AbortController();
    loopAbortRef.current = ac;
    const view = getView(projectId, pageId);
    if (!view) return;
    // SPEC-427 D: 继续修正轮承袭原请求路径标记 — 回溯最近一条 user 消息判定 insistsNoReference。
    const lastUser = [...view.project.chat].reverse().find(m => m.role === 'user');
    const noRef = lastUser ? isNoRefReplicaTurn(lastUser.text, !!lastUser.images?.length) : false;
    dispatch({ type: 'AGENT_START' });
    const refineMsg = buildRefineInstruction(residual);
    let r2: AgentResult;
    try {
      r2 = await runTurn({ message: refineMsg, canvas: view.page.canvas, chat: view.project.chat, needsName: false, extra: { projectId }, ac, alive: () => gen === loopGenRef.current });
    } catch (e: any) {
      if (gen !== loopGenRef.current || isAbortError(e)) return;
      dispatch({ type: 'AGENT_ERROR', error: e?.message || String(e), retryText: refineMsg, projectId });
      return;
    }
    if (gen !== loopGenRef.current) return;
    dispatch({ type: 'AGENT_RESULT', projectId, pageId, reply: r2.reply, ops: r2.ops, jobs: r2.jobs, error: r2.error, noReference: noRef });
    relayOps(r2.ops);
    if (r2.error) return; // AGENT_RESULT 已渲染可重试错误气泡
    r2.jobs.forEach(j => watchJob(j.id));
    void runReviewLoop({ gen, ac, projectId, pageId, instruction: refineMsg, jobIds: r2.jobs.map(j => j.id), draftSummary: r2.fact_summary, noRef });
  }, [project, page, getView, runTurn, relayOps, watchJob, runReviewLoop, dispatch, t]);

  const handleRetry = useCallback(async (jobId: string) => {
    try {
      await retryJob(jobId);
      watchJob(jobId);
    } catch (e: any) {
      dispatch({ type: 'AGENT_ERROR', error: `${e?.message || String(e)}` });
    }
  }, [watchJob]);

  const handleExport = useCallback(() => {
    const stage = stageRef.current;
    if (!stage) return;
    try {
      const scale = stage.scaleX() || 1;
      const uri = stage.toDataURL({ pixelRatio: 1 / scale, mimeType: 'image/png' });
      const a = document.createElement('a');
      a.href = uri;
      a.download = `design-${Date.now()}.png`;
      a.click();
    } catch {
      setExportError(t('canvas.exportFailed'));
    }
  }, [t]);

  const handleLogout = useCallback(async () => {
    await postLogout();
    setExpired(false);
    setAuth('anon');
    setMe(null);
  }, []);

  const onPatch = useCallback((id: string, patch: Partial<CanvasObject>) => {
    dispatch({ type: 'PATCH_OBJECT', id, patch });
  }, []);

  // Delete/Backspace removes the selection (suppressed while an input has focus)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Delete' && e.key !== 'Backspace') return;
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || (e.target as HTMLElement | null)?.isContentEditable) return;
      if (state.selectedIds.length > 0) {
        e.preventDefault();
        dispatch({ type: 'REMOVE_MANY', ids: state.selectedIds });
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [state.selectedIds]);

  // ===== Region-select local editing =====
  // Esc exits selection/instruction states (but not while executing); with no region state, Esc exits preview
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || regionBusy) return;
      if (regionTarget || regionPending) {
        setRegionTarget(null); setRegionPending(null); setRegionError(null);
      } else if (preview) {
        setPreview(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [regionTarget, regionPending, regionBusy, preview]);

  const handleRegionSelect = useCallback((relRect: RegionRect) => {
    if (!regionTarget || !project || !page) return;
    const obj = page.canvas.objects.find(o => o.id === regionTarget);
    if (!obj || obj.type !== 'image' || !obj.src || !obj.w || !obj.h) return;
    // Open the composer instantly — pixel conversion is deferred to submit time;
    // previously we waited for naturalSize to load the image here (proxied cross-origin large images load slowly —
    // that was the root cause of the composer taking forever to appear).
    setRegionPending({ objectId: regionTarget, relRect });
    setRegionError(null);
  }, [regionTarget, project, page]);

  const handleRegionSubmit = useCallback(async (instruction: string) => {
    if (!regionPending || !project || !page) return;
    const { objectId, relRect } = regionPending;
    const obj = page.canvas.objects.find(o => o.id === objectId);
    if (!obj || obj.type !== 'image' || !obj.src || !obj.w || !obj.h) return;
    const origSrc = obj.src;
    setRegionBusy(true); setRegionError(null);
    // Scoped to the originating project/page (captured now): if the user switches projects mid-generation the
    // async callbacks below still land back here instead of patching whatever page happens to be open (SPEC-371).
    const originProjectId = project.id;
    const originPageId = page.id;
    dispatch({ type: 'PATCH_OBJECT', id: objectId, patch: { pending: true }, projectId: originProjectId, pageId: originPageId }); // switch to the generating overlay immediately
    try {
      // Pixel conversion moved here — the natural image size is only needed at submit time,
      // and it shares one image load with the cropRegion call right after, so the composer no longer blocks.
      const nat = await naturalSize(origSrc);
      const pixelRect: RegionRect = {
        x: Math.round(relRect.x / obj.w * nat.w),
        y: Math.round(relRect.y / obj.h * nat.h),
        w: Math.max(1, Math.round(relRect.w / obj.w * nat.w)),
        h: Math.max(1, Math.round(relRect.h / obj.h * nat.h)),
      };
      // The edit model rejects inputs below 240x240 (SPEC-379: 400 InvalidParameter on thin regions like banner
      // strips). Upscale the crop to meet the minimum; compositeRegion scales the result back into the original
      // rect, so the upscale is invisible downstream. Genuinely tiny/extreme regions get a friendly error instead.
      const cropScale = Math.max(MIN_EDIT_PX / pixelRect.w, MIN_EDIT_PX / pixelRect.h, 1);
      if (cropScale > MAX_CROP_UPSCALE) throw new Error(t('region.tooSmall'));
      const cropData = await cropRegion(origSrc, pixelRect, cropScale);
      // SPEC-388: crops are transient edit inputs (only referenced while the job runs) → TTL them so
      // orphans don't accumulate in IMG_STORE forever.
      const cropUrl = await uploadImage(cropData, 3);
      const jobId = await postRegionEdit(cropUrl, instruction);
      dispatch({ type: 'JOB_UPDATE', job: { id: jobId, kind: 'edit', status: 'queued', attempts: 0, created_at: Date.now() } });
      // Note: do NOT attach job_id to the object — the reducer's JOB_UPDATE would auto-paste result_image (the cropped
      // patch) onto the object with the same job_id, racing the region-edit composite flow. The pollJob callback below
      // owns this flow's entire lifecycle.
      setRegionPending(null); setRegionTarget(null);
      pollJob(jobId, (job) => {
        dispatch({ type: 'JOB_UPDATE', job });
        if (job.status === 'done' && job.result_image) {
          void (async () => {
            try {
              const comp = await compositeRegion(origSrc, job.result_image!, pixelRect);
              const compUrl = await uploadImage(comp);
              dispatch({ type: 'PATCH_OBJECT', id: objectId, patch: { src: compUrl, pending: false, error: null }, projectId: originProjectId, pageId: originPageId });
              dispatch({ type: 'AGENT_RESULT', projectId: originProjectId, pageId: originPageId, reply: `${t('region.done')}${instruction}`, ops: [], jobs: [] });
            } catch (e: any) {
              dispatch({ type: 'PATCH_OBJECT', id: objectId, patch: { pending: false, error: e?.message || String(e) }, projectId: originProjectId, pageId: originPageId });
            } finally { setRegionBusy(false); }
          })();
        } else if (job.status === 'error') {
          dispatch({ type: 'PATCH_OBJECT', id: objectId, patch: { pending: false }, projectId: originProjectId, pageId: originPageId });
          setRegionBusy(false);
          setRegionPending({ objectId, relRect }); // reopening the instruction bar lets the user retry/cancel
          setRegionError(job.error || 'edit failed');
        }
      });
    } catch (e: any) {
      dispatch({ type: 'PATCH_OBJECT', id: objectId, patch: { pending: false }, projectId: originProjectId, pageId: originPageId });
      setRegionBusy(false);
      setRegionError(e?.message || String(e));
    }
  }, [regionPending, project, page, t]);

  const handleNewProject = useCallback((artboard?: { w: number; h: number }) => {
    dispatch({ type: 'PROJECT_NEW', name: `${t('project.untitled')} ${state.projects.length + 1}`, artboard });
  }, [state.projects.length, t]);

  const handleNewPage = useCallback(() => {
    if (!project) return;
    dispatch({ type: 'PAGE_NEW', name: String(project.pages.length + 1) });
  }, [project]);

  // Save component — use the localized default name when the name is empty
  const handleSaveComponent = useCallback((name: string) => {
    const fallback = translate(getLang(), 'side.components.defaultName', { n: state.components.length + 1 });
    dispatch({ type: 'COMPONENT_SAVE', name: name.trim() || fallback });
  }, [state.components.length]);

  // ===== Auto-dismiss floating panels on outside click =====
  // Clicking anywhere outside the panels closes the floating props panel and the region instruction bar.
  // Sole exemption: the Konva canvas itself (.konvajs-content) — Konva handles that (blank = deselect → panel
  // disappears, object = switch selection → panel refreshes).
  // Earlier bug where outside clicks didn't dismiss: the old version exempted the whole .artboard-wrap, but the
  // gray margins around the canvas sit inside the wrap yet outside the stage — clicks there reached neither Konva
  // nor the dismiss handler. Fixed by exempting only the Konva container.
  useEffect(() => {
    const onDown = (e: PointerEvent) => {
      const el = e.target as HTMLElement | null;
      if (!el) return;
      if (el.closest('.props-float')) return;
      if (el.closest('.konvajs-content')) return; // Konva handles its own: blank deselect / object select / drag transform
      if (state.selectedIds.length > 0) dispatch({ type: 'SELECT', id: null });
      if (regionPending && !regionBusy && !el.closest('.region-composer')) {
        setRegionPending(null); setRegionTarget(null); setRegionError(null);
      } else if (regionTarget && !el.closest('.region-composer')) {
        setRegionTarget(null);
      }
    };
    window.addEventListener('pointerdown', onDown);
    return () => window.removeEventListener('pointerdown', onDown);
  }, [state.selectedIds.length, regionPending, regionBusy, regionTarget]);

  // ===== Undo/redo shortcuts: Ctrl/Cmd+Z undo, +Shift or Ctrl+Y redo =====
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable)) return;
      if (!(e.metaKey || e.ctrlKey)) return;
      const k = e.key.toLowerCase();
      if (k === 'z') { e.preventDefault(); dispatch({ type: e.shiftKey ? 'REDO' : 'UNDO' }); }
      else if (k === 'y') { e.preventDefault(); dispatch({ type: 'REDO' }); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // ===== Region instruction bar anchor: hugs the selection instead of sitting fixed at the bottom =====
  const regionAnchor = useMemo(() => {
    if (!regionPending || !page) return null;
    const stage = stageRef.current;
    const area = canvasAreaRef.current;
    const obj = page.canvas.objects.find(o => o.id === regionPending.objectId);
    if (!stage || !area || !obj) return null;
    const scale = stage.scaleX() || 1;
    const cr = stage.container().getBoundingClientRect();
    const ar = area.getBoundingClientRect();
    const { relRect } = regionPending;
    const cx = (cr.left - ar.left) + (obj.x + relRect.x + relRect.w / 2) * scale;
    const top = (cr.top - ar.top) + (obj.y + relRect.y) * scale;
    const bottom = (cr.top - ar.top) + (obj.y + relRect.y + relRect.h) * scale;
    return { cx, top, bottom, areaWidth: ar.width, areaHeight: ar.height };
  }, [regionPending, page]);

  // ===== Render =====
  if (auth === 'unknown') {
    return <div className="boot-splash" aria-hidden="true" />;
  }
  if (auth === 'anon') {
    return <LoginPage expired={expired} onAuthed={() => { setExpired(false); setAuth('authed'); }} />;
  }

  return (
    <div className={`app-shell ${preview ? 'is-preview' : ''}`}>
      <header className="topbar">
        <span className="brand">design place</span>
        {state.view === 'workbench' && project && (
          <ProjectSwitcher projectName={project.name} onHome={() => void goHome()} />
        )}
        <div className="spacer" />
        {state.view === 'workbench' && project && (
          <button
            className={`ghost-btn ${preview ? 'preview-on' : ''}`}
            onClick={() => setPreview(v => !v)}
            title={preview ? t('topbar.exitPreview') : t('topbar.preview')}
            aria-label={preview ? t('topbar.exitPreview') : t('topbar.preview')}
          >
            {preview ? <StopIcon /> : <PlayIcon />} {preview ? t('topbar.exitPreview') : t('topbar.preview')}
          </button>
        )}
        <button
          className="ghost-btn icon-btn topbar-menu-btn"
          onClick={() => setMenuOpen(v => !v)}
          aria-expanded={menuOpen}
          aria-label={t('topbar.menu')}
          title={t('topbar.menu')}
        >
          <MenuIcon />
        </button>
        {/* SPEC-468 M1: low-frequency actions — inline on desktop (wrapper is display:contents),
            collapsed behind the hamburger on mobile. Same buttons, same handlers (G2). */}
        <div className={`topbar-actions${menuOpen ? ' open' : ''}`} onClick={() => setMenuOpen(false)}>
          <button className="ghost-btn" onClick={handleExport} disabled={state.view !== 'workbench' || !project}>{t('topbar.export')}</button>
          <button className="ghost-btn" onClick={() => toggleLang()} title={t('topbar.lang.title')} aria-label={t('topbar.lang.title')}>
            {t('topbar.lang.label')}
          </button>
          <button
            className="ghost-btn icon-btn"
            onClick={() => toggleTheme()}
            title={t(theme === 'dark' ? 'topbar.theme.toLight' : 'topbar.theme.toDark')}
            aria-label={t(theme === 'dark' ? 'topbar.theme.toLight' : 'topbar.theme.toDark')}
          >
            {theme === 'dark' ? <SunIcon /> : <MoonIcon />}
          </button>
          {me?.role === 'admin' && (
            <button className="ghost-btn" onClick={() => setAdminOpen(true)}>{t('topbar.admin')}</button>
          )}
          <button className="ghost-btn" onClick={() => void handleLogout()}>{t('topbar.logout')}</button>
        </div>
      </header>

      {exportError && <div className="toast-error" role="alert"><AlertIcon /><span>{exportError}</span></div>}
      {(syncStatus === 'paused' || syncStatus === 'rejected') && (
        <div className="toast-error" role="status">{t(syncStatus === 'rejected' ? 'cloud.rejected' : 'cloud.paused')}</div>
      )}
      {(syncStatus === 'syncing' || syncStatus === 'retrying') && (
        <div className={`sync-indicator${syncStatus === 'retrying' ? ' is-retrying' : ''}`} role="status">
          <span className="sync-dot" aria-hidden="true" />
          {t(syncStatus === 'syncing' ? 'cloud.syncing' : 'cloud.retrying')}
        </div>
      )}

      {state.view === 'home' ? (
        <HomeGallery
          projects={state.projects}
          onOpen={openProject}
          onNew={ab => handleNewProject(ab)}
          onDelete={id => dispatch({ type: 'PROJECT_DELETE', id })}
          onRename={(id, name) => dispatch({ type: 'PROJECT_RENAME', id, name })}
        />
      ) : project && page ? (
        <div className={`workspace tab-${mobileTab} ${preview ? 'is-preview' : ''}`}>
          {/* Immersive preview (both side panels collapsed): exit via the floating X; Esc works too (existing key handler) */}
          {preview && (
            <button
              className="preview-exit"
              onClick={() => setPreview(false)}
              title={`${t('topbar.exitPreview')} (Esc)`}
              aria-label={t('topbar.exitPreview')}
            >
              <XIcon size={14} />
            </button>
          )}
          <ChatPanel chat={project.chat} busy={state.busy} onSend={(text, images) => void handleSend(text, images)} onRetry={handleRetryChat} collapsed={chatCollapsed && !narrow} onToggleCollapse={() => setChatCollapsed(v => !v)} loopActive={loopActive} onStop={handleStopLoop} progress={turnProgress} onContinueRefine={(residual, frozen) => void handleContinueRefine(residual, frozen)} onPlanRetry={() => { const lastUser = [...project.chat].reverse().find(m => m.role === 'user'); if (lastUser) void handleSend(lastUser.text, [], false, 'mutate'); }} />
          <main className="canvas-area" ref={canvasAreaRef as React.RefObject<HTMLElement>}>
            {/* PageBar + undo/redo on one row — buttons no longer float over the canvas (no occlusion); divider matches the panel-header height on both sides */}
            <div className="canvas-topbar">
              {/* SPEC-380: collapsed-panel expand buttons live inside the topbar row — flex pushes the page tabs
                  (left) and undo/redo (right) aside instead of overlapping them. */}
              {!preview && chatCollapsed && !narrow && (
                <button className="panel-rail-btn" onClick={() => setChatCollapsed(false)} title={t('panel.expand.chat')} aria-label={t('panel.expand.chat')}><ChevronRightIcon /></button>
              )}
              <PageBar
                pages={project.pages}
                currentPageId={page.id}
                onSwitch={id => dispatch({ type: 'PAGE_SWITCH', id })}
                onAdd={handleNewPage}
                onRename={(id, name) => dispatch({ type: 'PAGE_RENAME', id, name })}
                onDelete={id => dispatch({ type: 'PAGE_DELETE', id })}
              />
              {!preview && (
                <div className="history-bar">
                  <button className="ghost-btn icon-btn" onClick={() => dispatch({ type: 'UNDO' })} disabled={state.past.length === 0} title={t('topbar.undo')} aria-label={t('topbar.undo')}><UndoIcon /></button>
                  <button className="ghost-btn icon-btn" onClick={() => dispatch({ type: 'REDO' })} disabled={state.future.length === 0} title={t('topbar.redo')} aria-label={t('topbar.redo')}><RedoIcon /></button>
                </div>
              )}
              {!preview && sideCollapsed && !narrow && (
                <button className="panel-rail-btn" onClick={() => setSideCollapsed(false)} title={t('panel.expand.layers')} aria-label={t('panel.expand.layers')}><ChevronLeftIcon /></button>
              )}
            </div>
            <Artboard
              canvas={page.canvas}
              selectedId={state.selectedId}
              selectedIds={state.selectedIds}
              jobs={state.jobs}
              onSelect={(id, additive) => {
                dispatch({ type: 'SELECT', id, additive });
                // Default-collapsed behavior (SPEC-374): a fresh single-object selection always shows the panel collapsed.
                // Same-object re-clicks and shift-additive multi-selects don't disturb the current expanded state.
                if (id && !additive && id !== state.selectedId) setPropsCollapsed(true);
              }}
              onMultiSelect={(ids, additive) => dispatch({ type: 'SELECT_MANY', ids, additive })}
              onPatch={onPatch}
              onMoveMany={(id, x, y) => dispatch({ type: 'MOVE_MANY', id, x, y, ids: state.selectedIds })}
              stageRef={stageRef}
              regionTarget={preview ? null : regionTarget}
              onRegionSelect={(r) => void handleRegionSelect(r)}
              preview={preview}
              onNavigate={(pageId) => dispatch({ type: 'PAGE_SWITCH', id: pageId })}
            />
            {/* Region drag-marquee hint moved out of Artboard and mounted centered at the bottom of canvas-area
                (position:relative) — inside the artboard container it was clipped by overflow and the bubble got cut off;
                now it also stops scaling with the canvas. Shown only when region mode is active (same check as
                regionTarget inside Artboard). */}
            {!preview && regionTarget && page.canvas.objects.some(o => o.id === regionTarget && o.type === 'image') && (
              <div className={`region-hint${hintVisible ? '' : ' region-hint-hidden'}`} aria-hidden={!hintVisible}><span>{t('region.hint')}</span></div>
            )}
            {regionPending && (
              <RegionComposer
                busy={regionBusy}
                error={regionError}
                anchor={regionAnchor}
                onSubmit={(ins) => void handleRegionSubmit(ins)}
                onCancel={() => { setRegionPending(null); setRegionTarget(null); setRegionError(null); }}
              />
            )}
            {/* Floating props panel — no longer follows the layer list */}
            {!preview && state.selectedIds.length > 0 && (
              <PropsFloat
                objects={page.canvas.objects}
                selectedId={state.selectedId}
                selectedIds={state.selectedIds}
                jobs={state.jobs}
                onPatch={onPatch}
                onRetryJob={handleRetry}
                onRegionEdit={(id) => { setRegionTarget(id); setRegionPending(null); setRegionError(null); }}
                pages={project.pages}
                currentPageId={page.id}
                onGroup={() => dispatch({ type: 'GROUP' })}
                onUngroup={() => dispatch({ type: 'UNGROUP' })}
                onDeleteSelected={() => dispatch({ type: 'REMOVE_MANY', ids: state.selectedIds })}
                onAlign={(dir) => dispatch({ type: 'ALIGN_MANY', dir })}
                onClose={() => dispatch({ type: 'SELECT', id: null })}
                collapsed={propsCollapsed}
                onToggleCollapse={() => setPropsCollapsed(v => !v)}
              />
            )}
          </main>
          <SidePanel
            objects={page.canvas.objects}
            selectedId={state.selectedId}
            selectedIds={state.selectedIds}
            jobs={state.jobs}
            onSelect={(id, additive) => dispatch({ type: 'SELECT', id, additive })}
            onReorder={(id, dir) => dispatch({ type: 'REORDER', id, dir })}
            onReorderTo={(id, toIndex) => dispatch({ type: 'REORDER_TO', id, toIndex })}
            onRemove={(id) => dispatch({ type: 'REMOVE', id })}
            onToggleVisible={(id) => dispatch({ type: 'TOGGLE_VISIBLE', id })}
            onRename={(id, name) => dispatch({ type: 'RENAME_OBJECT', id, name })}
            artboard={page.canvas.artboard}
            onCanvasResize={(w, h) => dispatch({ type: 'CANVAS_RESIZE', w, h })}
            onCanvasBg={(color) => dispatch({ type: 'CANVAS_BG', color })}
            components={state.components}
            onSaveComponent={handleSaveComponent}
            onInsertComponent={(id) => dispatch({ type: 'COMPONENT_INSERT', id })}
            onDeleteComponent={(id) => dispatch({ type: 'COMPONENT_DELETE', id })}
            collapsed={sideCollapsed && !narrow}
            onToggleCollapse={() => setSideCollapsed(v => !v)}
          />
          <nav className="mobile-nav" aria-label="mobile tabs">
            <button className={mobileTab === 'chat' ? 'active' : ''} onClick={() => setMobileTab('chat')}>
              <ChatIcon /><span>{t('nav.chat')}</span>
            </button>
            <button className={mobileTab === 'canvas' ? 'active' : ''} onClick={() => setMobileTab('canvas')}>
              <FrameIcon /><span>{t('nav.canvas')}</span>
            </button>
            <button className={mobileTab === 'layers' ? 'active' : ''} onClick={() => setMobileTab('layers')}>
              <LayersIcon /><span>{t('nav.layers')}</span>
            </button>
          </nav>
        </div>
      ) : (
        <main className="empty-state">
          <div className="empty-card">
            <p className="empty-title">{t('project.none.title')}</p>
            <p className="hint">{t('project.none.hint')}</p>
            <button className="primary-btn" onClick={() => handleNewProject()}><PlusIcon /> {t('project.new')}</button>
          </div>
        </main>
      )}
      {adminOpen && me && (
        <AdminPanel currentUser={me.username} onClose={() => setAdminOpen(false)} />
      )}
    </div>
  );
}
